import { useEffect, useState, type FormEvent } from "react";
import {
  ArrowLeftIcon,
  FingerprintSimpleIcon,
  KeyIcon,
  LockKeyIcon,
  PaperPlaneTiltIcon,
  UserCircleIcon
} from "@phosphor-icons/react";
import { ApiError, api } from "../api";

type LoginStep = "identity" | "code";

function authError(error: unknown, step: LoginStep): string {
  if (error instanceof ApiError) {
    if (error.status === 429) return "Слишком много попыток. Подождите несколько минут.";
    if (error.status === 503) {
      return "Сервис входа временно недоступен. Нажмите Start у @chatguvenlikbot и повторите.";
    }
    if (error.status === 400 && step === "code") return "Код неверный или уже истёк.";
  }
  return "Не удалось проверить Telegram. Повторите попытку.";
}

export function LoginPage({ onAuthenticated }: { onAuthenticated: () => Promise<void> }) {
  const [step, setStep] = useState<LoginStep>("identity");
  const [login, setLogin] = useState("");
  const [code, setCode] = useState("");
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (step !== "code" || secondsLeft <= 0) return;
    const timer = window.setInterval(() => setSecondsLeft((value) => Math.max(0, value - 1)), 1000);
    return () => window.clearInterval(timer);
  }, [step, secondsLeft]);

  async function requestCode(event: FormEvent) {
    event.preventDefault();
    setPending(true);
    setError("");
    try {
      const challenge = await api.startLogin(login.trim());
      setSecondsLeft(challenge.expires_in);
      setCode("");
      setStep("code");
    } catch (requestError) {
      setError(authError(requestError, "identity"));
    } finally {
      setPending(false);
    }
  }

  async function verifyCode(event: FormEvent) {
    event.preventDefault();
    setPending(true);
    setError("");
    try {
      await api.verifyLogin(code);
      await onAuthenticated();
    } catch (requestError) {
      setError(authError(requestError, "code"));
    } finally {
      setPending(false);
    }
  }

  function changeIdentity() {
    setStep("identity");
    setCode("");
    setSecondsLeft(0);
    setError("");
  }

  const timer = `${Math.floor(secondsLeft / 60)}:${String(secondsLeft % 60).padStart(2, "0")}`;

  return (
    <main className="login-page">
      <section className="login-copy-panel">
        <header className="login-brand"><img className="brand-logo" src="/guvenlik-poster.webp" alt="" /><div><strong>ChatGuvenlik</strong><small>панель модерации</small></div></header>
        <div className="login-message"><samp>ТОЛЬКО ДЛЯ АДМИНИСТРАТОРОВ</samp><h1>Вход в панель</h1><p>Введите Telegram ID или username. Код придёт в личные сообщения от бота.</p></div>

        {step === "identity" ? (
          <form className="login-auth-form" onSubmit={requestCode}>
            <label htmlFor="telegram-login">Telegram ID или username</label>
            <div className="login-auth-field">
              <UserCircleIcon size={21} weight="thin" />
              <input
                id="telegram-login"
                value={login}
                onChange={(event) => setLogin(event.target.value)}
                placeholder="ID или @username"
                minLength={3}
                maxLength={64}
                autoComplete="username"
                autoCapitalize="none"
                spellCheck={false}
                required
                autoFocus
              />
            </div>
            <button className="button primary login-button" disabled={pending}>
              <PaperPlaneTiltIcon size={19} />
              {pending ? "Отправляю код" : "Получить код"}
            </button>
          </form>
        ) : (
          <form className="login-auth-form" onSubmit={verifyCode}>
            <div className="login-code-header">
              <div><samp>КОД ОТПРАВЛЕН</samp><strong>Проверьте Telegram</strong></div>
              <time dateTime={`PT${secondsLeft}S`}>{timer}</time>
            </div>
            <label htmlFor="telegram-code">Шестизначный код</label>
            <div className="login-auth-field login-code-field">
              <KeyIcon size={21} weight="thin" />
              <input
                id="telegram-code"
                value={code}
                onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="000000"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]{6}"
                maxLength={6}
                required
                autoFocus
              />
            </div>
            <button className="button primary login-button" disabled={pending || code.length !== 6 || secondsLeft === 0}>
              <FingerprintSimpleIcon size={19} />
              {pending ? "Проверяю" : secondsLeft === 0 ? "Код истёк" : "Подтвердить вход"}
            </button>
            <button className="login-back" type="button" onClick={changeIdentity}>
              <ArrowLeftIcon size={15} />Ввести другой аккаунт
            </button>
          </form>
        )}

        {error && <div className="login-error" role="alert" aria-live="assertive">{error}</div>}
        <p className="login-note"><LockKeyIcon size={16} />Войти могут только пользователи из списка доступа.</p>
      </section>
      <aside className="login-telemetry" aria-label="Анимация ChatGuvenlik">
        <div className="telemetry-poster">
          <div className="hero-motion" aria-hidden="true">
            <img className="hero-motion-poster" src="/guvenlik-poster.webp" alt="" />
            <video autoPlay muted loop playsInline preload="auto" poster="/guvenlik-poster.webp">
              <source src="/guvenlik.mp4" type="video/mp4" />
            </video>
          </div>
        </div>
      </aside>
    </main>
  );
}
